import { Component } from '@theme/component';
import { fetchConfig, onAnimationEnd, preloadImage } from '@theme/utilities';
import { ThemeEvents, CartAddEvent, CartErrorEvent, CartUpdateEvent, VariantUpdateEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

export const ADD_TO_CART_TEXT_ANIMATION_DURATION = 2000;
export const REMOVE_FROM_CART_TEXT_ANIMATION_DURATION = 1000;

/**
 * Finds all add-to-cart buttons for the same product on the page
 * @param {HTMLButtonElement} button - Reference button to find matching buttons
 * @returns {HTMLButtonElement[]} Array of all matching add-to-cart buttons
 */
function findAllAddToCartButtons(button) {
  // Find the product-form-component that contains this button
  const productForm = button.closest('product-form-component');
  if (!productForm || !(productForm instanceof HTMLElement)) return [button];

  const productId = productForm.dataset.productId;
  if (!productId) return [button];

  // Find all product-form-components with the same product ID
  const allProductForms = document.querySelectorAll(`product-form-component[data-product-id="${productId}"]`);
  /** @type {HTMLButtonElement[]} */
  const allButtons = [];

  allProductForms.forEach((form) => {
    if (!(form instanceof HTMLElement)) return;
    const addToCartComponent = form.querySelector('add-to-cart-component');
    if (addToCartComponent && 'refs' in addToCartComponent && addToCartComponent.refs && typeof addToCartComponent.refs === 'object' && addToCartComponent.refs !== null && 'addToCartButton' in addToCartComponent.refs) {
      const btn = addToCartComponent.refs.addToCartButton;
      if (btn instanceof HTMLButtonElement) {
        allButtons.push(btn);
      }
    }
  });

  return allButtons.length > 0 ? allButtons : [button];
}

/**
 * Determines the "ADDED" text based on button context.
 * Returns "ADDED" if button is inside accordion-custom or product-recommendations,
 * otherwise returns "ADDED TO CART".
 * @param {HTMLButtonElement} button - The add to cart button
 * @returns {string} The text to display
 */
function getAddedToCartText(button) {
  if (!button) return 'ADDED TO CART';

  // Check if button is inside accordion-custom or product-recommendations
  const isInAccordion = button.closest('accordion-custom') !== null;
  const isInProductRecommendations = button.closest('product-recommendations') !== null;

  if (isInAccordion || isInProductRecommendations) {
    return 'ADDED';
  }

  return 'ADDED TO CART';
}

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];

  /** @type {number | undefined} */
  #animationTimeout;

  /** @type {number | undefined} */
  #cleanupTimeout;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerenter', this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);
    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    const form = this.closest('form');
    if (!form?.checkValidity()) return;

    // Check if product is in cart (button has atc-in-cart class)
    const { addToCartButton } = this.refs;
    const isInCart = addToCartButton.classList.contains('atc-in-cart');

    // Only show "ADDED" animation if product is not in cart (will be added)
    if (!isInCart) {
      this.animateAddToCart();

      const animationEnabled = this.dataset.addToCartAnimation === 'true';

      if (animationEnabled && !event.target.closest('.quick-add-modal')) {
        this.#animateFlyToCart();
      }
    } else {
      // If product is in cart, show "REMOVING" immediately
      this.animateRemoveFromCart();

      const animationEnabled = this.dataset.addToCartAnimation === 'true';

      if (animationEnabled && !event.target.closest('.quick-add-modal')) {
        this.#animateFlyFromCart();
      }
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the fly from cart animation (reverse direction).
   */
  #animateFlyFromCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyFromCartElement = /** @type {FlyToCart} */ (document.createElement('fly-to-cart'));

    flyFromCartElement.style.setProperty('background-image', `url(${image})`);
    // Reverse: source is cart, destination is button
    flyFromCartElement.source = cartIcon;
    flyFromCartElement.destination = addToCartButton;
    flyFromCartElement.setAttribute('reverse', 'true');

    document.body.appendChild(flyFromCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart() {
    const { addToCartButton } = this.refs;
    const allButtons = findAllAddToCartButtons(addToCartButton);

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);

    // Always show temporary "ADDED" state on all buttons, even if atc-added class already exists
    allButtons.forEach((button) => {
      // Remove atc-removing if present
      button.classList.remove('atc-removing');

      // Ensure atc-added class is present
      button.classList.add('atc-added');

      // Always set temporary "ADDING..." text (even if class was already there)
      const addedText = button.querySelector('.add-to-cart-text--added');
      if (addedText) {
        const textSpan = addedText.querySelector('span:last-child');
        if (textSpan) {
          textSpan.textContent = 'ADDING...';
        }
        // Ensure checkmark is visible
        const checkmarkIcon = addedText.querySelector('.add-to-cart-icon--added');
        if (checkmarkIcon instanceof HTMLElement) {
          checkmarkIcon.style.display = '';
        }
      }
    });

    // After animation, check if we should keep the state
    this.#animationTimeout = setTimeout(() => {
      allButtons.forEach((button) => {
        // Check if button has atc-in-cart class (set by ProductFormComponent)
        if (button.classList.contains('atc-in-cart')) {
          // Keep the added state and ensure text is correct based on context
          const addedText = button.querySelector('.add-to-cart-text--added');
          if (addedText) {
            const textSpan = addedText.querySelector('span:last-child');
            if (textSpan) textSpan.textContent = getAddedToCartText(button);
          }
        } else {
          // Remove added state if not in cart
          this.#cleanupTimeout = setTimeout(() => {
            button.classList.remove('atc-added');
          }, 10);
        }
      });
    }, ADD_TO_CART_TEXT_ANIMATION_DURATION);
  }

  /**
   * Animates the remove from cart button (shows "REMOVING" without checkmark).
   */
  animateRemoveFromCart() {
    const { addToCartButton } = this.refs;
    const allButtons = findAllAddToCartButtons(addToCartButton);

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);

    // Show "REMOVING" state without checkmark on all buttons
    allButtons.forEach((button) => {
      button.classList.add('atc-removing');

      // Hide the checkmark icon
      const addedText = button.querySelector('.add-to-cart-text--added');
      if (addedText instanceof HTMLElement) {
        const checkmarkIcon = addedText.querySelector('.add-to-cart-icon--added');
        if (checkmarkIcon instanceof HTMLElement) {
          checkmarkIcon.style.display = 'none';
        }

        const textSpan = addedText.querySelector('span:last-child');
        if (textSpan instanceof HTMLElement) {
          textSpan.textContent = 'REMOVING...';
        }
      }

      // Show the removing text
      if (!button.classList.contains('atc-added')) {
        button.classList.add('atc-added');
      }
    });

    // After animation, remove all states and return to normal
    this.#animationTimeout = setTimeout(() => {
      this.#cleanupTimeout = setTimeout(() => {
        allButtons.forEach((button) => {
          // Remove all animation-related classes
          button.classList.remove('atc-added', 'atc-in-cart', 'atc-removing');

          // Restore checkmark icon visibility
          const addedText = button.querySelector('.add-to-cart-text--added');
          if (addedText instanceof HTMLElement) {
            const checkmarkIcon = addedText.querySelector('.add-to-cart-icon--added');
            if (checkmarkIcon instanceof HTMLElement) {
              checkmarkIcon.style.display = '';
            }
            // Reset text to default based on context for next time
            const textSpan = addedText.querySelector('span:last-child');
            if (textSpan instanceof HTMLElement) {
              textSpan.textContent = getAddedToCartText(button);
            }
          }
        });
      }, 10);
    }, REMOVE_FROM_CART_TEXT_ANIMATION_DURATION);
  }
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

/**
 * Shared state storage for product cart status by variant ID
 * @type {Map<string, boolean>}
 */
const productCartState = new Map();

/**
 * A custom element that manages a product form.
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  /** @type {boolean} */
  #isInCart = false;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.variantSelected, this.#onVariantSelected, { signal });
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate, { signal });

    // Check if product is in cart on load
    this.#checkCartStatus();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
  }

  /**
   * Fetches the current cart state
   * @returns {Promise<{items?: Array<{id?: number; variant_id?: number}>}>}
   */
  async #fetchCart() {
    const response = await fetch(Theme.routes.cart_url + '.js');
    return response.json();
  }

  /**
   * Checks if the current variant is in the cart
   */
  async #checkCartStatus() {
    const variantId = this.refs.variantId?.value;
    if (!variantId) return;

    try {
      const cart = await this.#fetchCart();
      const isInCart = cart.items?.some(
        (item) => item.variant_id?.toString() === variantId.toString()
      ) || false;

      this.#isInCart = isInCart;
      this.#updateButtonState(isInCart, true); // Skip animation on initial load
    } catch (error) {
      console.error('Error checking cart status:', error);
    }
  }

  /**
   * Updates the button state based on cart status
   * @param {boolean} isInCart - Whether the product is in cart
   * @param {boolean} skipAnimation - Whether to skip the animation (for initial load)
   */
  #updateButtonState(isInCart, skipAnimation = false) {
    const button = this.refs.addToCartButtonContainer?.refs.addToCartButton;
    if (!button) return;

    const allButtons = findAllAddToCartButtons(button);

    allButtons.forEach((btn) => {
      if (isInCart) {
        btn.classList.add('atc-in-cart');
        const addedText = btn.querySelector('.add-to-cart-text--added');
        if (addedText) {
          const textSpan = addedText.querySelector('span:last-child');
          if (textSpan) {
            if (skipAnimation) {
              // For initial load, show correct text immediately based on context
              textSpan.textContent = getAddedToCartText(btn);
              btn.classList.add('atc-added');
            } else {
              // First show "ADDING...", then change to correct text after animation
              textSpan.textContent = 'ADDING...';
              setTimeout(() => {
                textSpan.textContent = getAddedToCartText(btn);
              }, ADD_TO_CART_TEXT_ANIMATION_DURATION);
            }
          }
        }
      } else {
        btn.classList.remove('atc-in-cart', 'atc-added');
      }
    });
  }

  /**
   * Updates the internal cart state (public method for synchronization)
   * @param {boolean} isInCart - Whether the variant is in cart
   */
  updateCartState(isInCart) {
    this.#isInCart = isInCart;
  }

  /**
   * Synchronizes cart state across all ProductFormComponent instances for the same variant
   * @param {string} variantId - The variant ID
   * @param {boolean} isInCart - Whether the variant is in cart
   * @param {boolean} skipButtonUpdate - Whether to skip button visual updates (for animations)
   */
  #syncCartState(variantId, isInCart, skipButtonUpdate = false) {
    if (!variantId) return;

    // Update shared state
    productCartState.set(variantId, isInCart);

    // Update this instance
    this.#isInCart = isInCart;

    // Update all other ProductFormComponent instances with the same variant
    const allProductForms = document.querySelectorAll('product-form-component');
    allProductForms.forEach((form) => {
      if (form === this) return; // Skip self
      if (!(form instanceof ProductFormComponent)) return;

      const formVariantId = form.refs.variantId?.value;
      if (formVariantId === variantId) {
        // Update the other instance's internal state
        form.updateCartState(isInCart);

        // Only update button visuals if not skipping (e.g., during animations)
        if (!skipButtonUpdate) {
          const button = form.refs.addToCartButtonContainer?.refs.addToCartButton;
          if (button) {
            const allButtons = findAllAddToCartButtons(button);
            allButtons.forEach((btn) => {
              // Don't update buttons that are in the middle of an animation
              if (btn.classList.contains('atc-removing')) return;

              // Don't update buttons that are showing temporary "ADDED" text (waiting for animation to complete)
              const addedText = btn.querySelector('.add-to-cart-text--added');
              if (addedText) {
                const textSpan = addedText.querySelector('span:last-child');
                if (textSpan && textSpan.textContent === 'ADDING...' && btn.classList.contains('atc-added')) {
                  return; // Button is in the middle of showing "ADDING...", don't interfere
                }
              }

              if (isInCart) {
                btn.classList.add('atc-in-cart');
                // Only update text if button is not in temporary "ADDED" state (waiting for animation)
                if (!btn.classList.contains('atc-added')) {
                  if (addedText) {
                    const textSpan = addedText.querySelector('span:last-child');
                    if (textSpan) {
                      textSpan.textContent = getAddedToCartText(btn);
                      btn.classList.add('atc-added');
                    }
                  }
                }
              } else {
                btn.classList.remove('atc-in-cart', 'atc-added');
              }
            });
          }
        }
      }
    });
  }

  /**
   * Gets the current cart state for a variant (checks shared state first, then actual cart)
   * @param {string} variantId - The variant ID to check
   * @returns {Promise<boolean>} Whether the variant is in cart
   */
  async #getCartState(variantId) {
    if (!variantId) return false;

    // First check shared state (faster)
    if (productCartState.has(variantId)) {
      return productCartState.get(variantId) || false;
    }

    // If not in shared state, check actual cart
    try {
      const cart = await this.#fetchCart();
      const isInCart = cart.items?.some(
        (item) => item.variant_id?.toString() === variantId.toString()
      ) || false;

      // Update shared state
      productCartState.set(variantId, isInCart);
      return isInCart;
    } catch (error) {
      console.error('Error checking cart state:', error);
      return false;
    }
  }

  /**
   * Removes the product from cart
   * @param {string} variantId - The variant ID to remove
   * @returns {Promise<{status?: number; message?: string; sections?: Record<string, string>}>}
   */
  async #removeFromCart(variantId) {
    const cart = await this.#fetchCart();
    const itemIndex = cart.items?.findIndex(
      (item) => item.variant_id?.toString() === variantId.toString()
    );

    if (itemIndex === -1 || itemIndex === undefined) {
      throw new Error('Item not found in cart');
    }

    // Line number is 1-based index
    const line = itemIndex + 1;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set();
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: 0,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    const response = await fetch(Theme.routes.cart_change_url, fetchConfig('json', { body }));

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Failed to remove from cart:', response.status, errorText);
      throw new Error(`Failed to remove from cart: ${response.status}`);
    }

    const responseText = await response.text();

    try {
      return JSON.parse(responseText);
    } catch (error) {
      // If response is not JSON, return empty object
      console.error('Failed to parse cart response as JSON:', error);
      return { sections: {} };
    }
  }

  /**
   * Handles the submit event for the product form.
   *
   * @param {Event} event - The submit event.
   */
  async handleSubmit(event) {
    const { addToCartTextError } = this.refs;
    // Stop default behaviour from the browser
    event.preventDefault();

    if (this.#timeout) clearTimeout(this.#timeout);

    // Check if the add to cart button is disabled and do an early return if it is
    if (this.refs.addToCartButtonContainer?.refs.addToCartButton?.getAttribute('disabled') === 'true') return;

    // Send the add to cart information to the cart
    const form = this.querySelector('form');

    if (!form) throw new Error('Product form element missing');

    const formData = new FormData(form);
    const variantId = formData.get('id')?.toString();

    if (!variantId) return;

    // Check actual cart state before action (more reliable than #isInCart alone)
    const isInCart = await this.#getCartState(variantId);

    // Check if any button is currently in removing animation
    const button = this.refs.addToCartButtonContainer?.refs.addToCartButton;
    const isRemoving = button?.classList.contains('atc-removing') || false;
    const isAdding = button?.classList.contains('atc-added') && !button.classList.contains('atc-in-cart');

    // Sync state across all instances (skip button update if animation is in progress)
    // Don't update buttons if we're about to add (will be handled by animateAddToCart) or remove (will be handled by animateRemoveFromCart)
    this.#syncCartState(variantId, isInCart, isRemoving || isAdding);

    // If product is in cart, remove it instead
    if (isInCart) {
      this.#removeFromCart(variantId)
        .then((response) => {
          if (response.status) {
            // Error removing
            if (addToCartTextError) {
              addToCartTextError.classList.remove('hidden');
              const textNode = addToCartTextError.childNodes[2];
              if (textNode) {
                textNode.textContent = response.message || 'Error removing item';
              } else {
                const newTextNode = document.createTextNode(response.message || 'Error removing item');
                addToCartTextError.appendChild(newTextNode);
              }
            }
            return;
          }

          // Successfully removed - sync state across all instances (skip button update, animation handles it)
          this.#syncCartState(variantId, false, true);

          // Animation already started in handleClick, it will clean up the state after animation

          // Dispatch cart update event (use CartUpdateEvent to prevent drawer from opening)
          const cartItemsComponents = document.querySelectorAll('cart-items-component');
          /** @type {string[]} */
          const cartItemComponentsSectionIds = [];
          cartItemsComponents.forEach((item) => {
            if (item instanceof HTMLElement && item.dataset.sectionId) {
              cartItemComponentsSectionIds.push(item.dataset.sectionId);
            }
          });

          // Use CartUpdateEvent instead of CartAddEvent to prevent drawer from opening
          const sectionId = cartItemComponentsSectionIds[0] || '';
          this.dispatchEvent(
            new CartUpdateEvent({}, sectionId, {
              source: 'product-form-component',
              itemCount: 0,
              sections: response.sections || {},
            })
          );
        })
        .catch((error) => {
          console.error('Error removing from cart:', error);
        })
        .finally(() => {
          cartPerformance.measureFromEvent('remove:user-action', event);
        });

      return;
    }

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    let cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
      formData.append('sections', cartItemComponentsSectionIds.join(','));
    });

    const fetchCfg = fetchConfig('javascript', { body: formData });

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
      },
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(form.getAttribute('id') || '', response.message, response.description, response.errors)
          );

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove('hidden');

          // Reuse the text node if the user is spam-clicking
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode = document.createTextNode(response.message);
            addToCartTextError.appendChild(newTextNode);
          }

          // Create or get existing error live region for screen readers
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add('hidden');

            // Clear the announcement
            this.#clearLiveRegionText();
          }, 10000);

          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
          // because our back-end still adds the max allowed amount to the cart.
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
            })
          );

          return;
        } else {
          const id = formData.get('id');

          if (addToCartTextError) {
            addToCartTextError.classList.add('hidden');
            addToCartTextError.removeAttribute('aria-live');
          }

          if (!id) throw new Error('Form ID is required');

          // Add aria-live region to inform screen readers that the item was added
          if (this.refs.addToCartButtonContainer?.refs.addToCartButton) {
            const addToCartButton = this.refs.addToCartButtonContainer.refs.addToCartButton;
            const addedTextElement = addToCartButton.querySelector('.add-to-cart-text--added');
            const addedText = addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, 5000);
          }

          // Mark as in cart - sync state across all instances (skip button update, animation handles it)
          this.#syncCartState(variantId, true, true);

          // Set atc-in-cart class on ALL buttons BEFORE animation so animateAddToCart() knows to keep the state
          const button = this.refs.addToCartButtonContainer?.refs.addToCartButton;
          if (button) {
            const allButtons = findAllAddToCartButtons(button);
            allButtons.forEach((btn) => {
              // Only add class if not already added (to avoid interfering with animation)
              if (!btn.classList.contains('atc-in-cart')) {
                btn.classList.add('atc-in-cart');
              }
            });
          }

          // Show temporary "ADDED" animation first on all buttons
          // animateAddToCart() will handle the transition to "ADDED TO CART" after animation
          this.refs.addToCartButtonContainer?.animateAddToCart();

          this.dispatchEvent(
            new CartAddEvent({}, id.toString(), {
              source: 'product-form-component',
              itemCount: Number(formData.get('quantity')) || Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
              sections: response.sections,
            })
          );
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        // add more thing to do in here if needed.
        cartPerformance.measureFromEvent('add:user-action', event);
      });
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = '';
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  #onVariantUpdate = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId, addToCartButtonContainer } = this.refs;

    const currentAddToCartButton = addToCartButtonContainer?.refs.addToCartButton;
    const newAddToCartButton = event.detail.data.html.querySelector('[ref="addToCartButton"]');

    if (!currentAddToCartButton) return;

    // Update the button state
    if (event.detail.resource == null || event.detail.resource.available == false) {
      addToCartButtonContainer.disable();
      this.refs.acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
    } else {
      addToCartButtonContainer.enable();
      this.refs.acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
    }

    // Update the add to cart button text and icon
    if (newAddToCartButton) {
      // Save the original text content before morphing to prevent it from being replaced
      const originalTextContent = currentAddToCartButton.querySelector('.add-to-cart-text__content')?.textContent?.trim();
      const isVariantAvailable = event.detail.resource != null && event.detail.resource.available !== false;

      morph(currentAddToCartButton, newAddToCartButton);

      // Restore the original text content if the variant is available and the new text is "Add" (truncated version)
      if (originalTextContent && isVariantAvailable) {
        const textContentElement = currentAddToCartButton.querySelector('.add-to-cart-text__content');
        const newTextContent = textContentElement?.textContent?.trim();

        // Only restore if the new text is "Add" (likely from quick-add component) and original was longer
        if (textContentElement && newTextContent === 'Add' && originalTextContent.length > newTextContent.length) {
          textContentElement.textContent = originalTextContent;
        }
      }
    }

    // Update the variant ID
    variantId.value = event.detail.resource.id ?? '';

    // Set the data attribute for the add to cart button to the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      productVariantMedia &&
        addToCartButtonContainer?.setAttribute('data-product-variant-media', productVariantMedia + '&width=100');
    }

    // Check cart status for new variant
    this.#checkCartStatus();
  };

  /**
   * Disable the add to cart button while the UI is updating before #onVariantUpdate is called.
   * Accelerated checkout button is also disabled via its own event listener not exposed to the theme.
   */
  #onVariantSelected = () => {
    this.refs.addToCartButtonContainer?.disable();
  };

  /**
   * Handles cart update events (e.g., when item is removed from cart drawer)
   * @param {CartUpdateEvent} event - The cart update event
   */
  #onCartUpdate = async (event) => {
    // Only handle events from cart-items-component (removals from cart drawer)
    if (event.detail?.data?.source !== 'cart-items-component') return;

    const variantId = this.refs.variantId?.value;
    if (!variantId) return;

    // Always check real cart state (ignore cache) when cart is updated externally
    try {
      const cart = await this.#fetchCart();
      const isInCart = cart.items?.some(
        (item) => item.variant_id?.toString() === variantId.toString()
      ) || false;

      // Update cache with real state
      productCartState.set(variantId, isInCart);

      // If variant was removed from cart, update button state immediately without animation
      if (!isInCart) {
        // Update internal state for this instance
        this.#isInCart = false;

        // Update internal state for all ProductFormComponent instances with the same variant
        const allProductForms = document.querySelectorAll('product-form-component');
        allProductForms.forEach((form) => {
          if (!(form instanceof ProductFormComponent)) return;
          const formVariantId = form.refs.variantId?.value;
          if (formVariantId === variantId) {
            form.updateCartState(false);
          }
        });

        // Update all buttons for this variant immediately (no animation, bypass syncCartState checks)
        const button = this.refs.addToCartButtonContainer?.refs.addToCartButton;
        if (button) {
          const allButtons = findAllAddToCartButtons(button);
          allButtons.forEach((btn) => {
            // Remove all cart-related classes
            btn.classList.remove('atc-added', 'atc-in-cart', 'atc-removing');

            // Restore checkmark icon visibility
            const addedText = btn.querySelector('.add-to-cart-text--added');
            if (addedText instanceof HTMLElement) {
              const checkmarkIcon = addedText.querySelector('.add-to-cart-icon--added');
              if (checkmarkIcon instanceof HTMLElement) {
                checkmarkIcon.style.display = '';
              }
              // Reset text to default based on context for next time
              const textSpan = addedText.querySelector('span:last-child');
              if (textSpan instanceof HTMLElement) {
                textSpan.textContent = getAddedToCartText(btn);
              }
            }
          });
        }
      } else if (isInCart && !this.#isInCart) {
        // If variant was added to cart (unlikely from cart drawer, but handle it)
        this.#syncCartState(variantId, true, false);
      }
    } catch (error) {
      console.error('Error handling cart update:', error);
    }
  };
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}

class FlyToCart extends HTMLElement {
  /** @type {Element} */
  source;

  /** @type {Element} */
  destination;

  connectedCallback() {
    this.#animate();
  }

  #animate() {
    const rect = this.getBoundingClientRect();
    const sourceRect = this.source.getBoundingClientRect();
    const destinationRect = this.destination.getBoundingClientRect();
    const isReverse = this.hasAttribute('reverse');

    //Define bezier curve points
    // Maybe add half of the size of the flying thingy to the x and y to make it center properly
    const offset = {
      x: rect.width / 2,
      y: rect.height / 2,
    };
    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2 - offset.x,
      y: sourceRect.top + sourceRect.height / 2 - offset.y,
    };

    const endPoint = {
      x: destinationRect.left + destinationRect.width / 2 - offset.x,
      y: destinationRect.top + destinationRect.height / 2 - offset.y,
    };

    //Calculate the control points
    // For reverse animation, adjust control points to go from cart to button
    let controlPoint1, controlPoint2;
    if (isReverse) {
      // Reverse: start from cart, go down and right, end at button
      controlPoint1 = { x: startPoint.x + 300, y: startPoint.y + 100 }; // Go right 300px and down 100px
      controlPoint2 = { x: endPoint.x, y: endPoint.y + 200 }; // Go down 200px
    } else {
      // Normal: start from button, go up and left, end at cart
      controlPoint1 = { x: startPoint.x, y: startPoint.y - 200 }; // Go up 200px
      controlPoint2 = { x: endPoint.x - 300, y: endPoint.y - 100 }; // Go left 300px and up 100px
    }

    //Animation variables
    /** @type {number | null} */
    let startTime = null;
    const duration = 600; // 600ms

    this.style.opacity = '1';

    /**
     * Animates the flying thingy along the bezier curve.
     * @param {number} currentTime - The current time.
     */
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Calculate current position along the bezier curve
      const position = bezierPoint(progress, startPoint, controlPoint1, controlPoint2, endPoint);

      //Update the position of the flying thingy
      this.style.setProperty('--x', `${position.x}px`);
      this.style.setProperty('--y', `${position.y}px`);

      // Scale: for reverse, scale up as it approaches button; for normal, scale down as it approaches cart
      const scale = isReverse
        ? 0.5 + progress * 0.5  // Start small, grow to full size
        : 1 - progress * 0.5;     // Start full size, shrink to half

      this.style.setProperty('--scale', `${scale}`);

      //Continue the animation if not finished
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        //Fade out the flying thingy
        this.style.opacity = '0';
        onAnimationEnd(this, () => this.remove());
      }
    };

    // Position the flying thingy back to the start point
    this.style.setProperty('--x', `${startPoint.x}px`);
    this.style.setProperty('--y', `${startPoint.y}px`);

    //Start the animation
    requestAnimationFrame(animate);
  }
}

/**
 * Calculates a point on a cubic Bézier curve.
 * @param {number} t - The parameter value (0 <= t <= 1).
 * @param {{x: number, y: number}} p0 - The starting point (x, y).
 * @param {{x: number, y: number}} p1 - The first control point (x, y).
 * @param {{x: number, y: number}} p2 - The second control point (x, y).
 * @param {{x: number, y: number}} p3 - The ending point (x, y).
 * @returns {{x: number, y: number}} The point on the curve.
 */
function bezierPoint(t, p0, p1, p2, p3) {
  const cX = 3 * (p1.x - p0.x);
  const bX = 3 * (p2.x - p1.x) - cX;
  const aX = p3.x - p0.x - cX - bX;

  const cY = 3 * (p1.y - p0.y);
  const bY = 3 * (p2.y - p1.y) - cY;
  const aY = p3.y - p0.y - cY - bY;

  const x = aX * Math.pow(t, 3) + bX * Math.pow(t, 2) + cX * t + p0.x;
  const y = aY * Math.pow(t, 3) + bY * Math.pow(t, 2) + cY * t + p0.y;

  return { x, y };
}

if (!customElements.get('fly-to-cart')) {
  customElements.define('fly-to-cart', FlyToCart);
}
